import { useEffect, useState } from "react";

interface OpeningAnimationProps {
  onComplete: () => void;
}

export default function OpeningAnimation({ onComplete }: OpeningAnimationProps) {
  const [phase, setPhase] = useState<"logo" | "text" | "subtitle" | "fadeout" | "done">("logo");

  useEffect(() => {
    // Phase timeline (+2000ms)
    const t1 = setTimeout(() => setPhase("text"), 400);       // logo in → show title
    const t2 = setTimeout(() => setPhase("subtitle"), 1000);  // show subtitle
    const t3 = setTimeout(() => setPhase("fadeout"), 3400);   // start fade out
    const t4 = setTimeout(() => {
      setPhase("done");
      onComplete();
    }, 4200); // fully done

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      clearTimeout(t4);
    };
  }, [onComplete]);

  if (phase === "done") return null;

  return (
    <div
      className={`fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-black transition-opacity duration-700 ${
        phase === "fadeout" ? "opacity-0" : "opacity-100"
      }`}
    >
      {/* Subtle background particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute rounded-full bg-white/5"
            style={{
              width: `${Math.random() * 3 + 1}px`,
              height: `${Math.random() * 3 + 1}px`,
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animation: `float-particle ${3 + Math.random() * 4}s ease-in-out infinite`,
              animationDelay: `${Math.random() * 2}s`,
            }}
          />
        ))}
      </div>

      {/* Center content */}
      <div className="relative flex flex-col items-center gap-6">
        {/* Logo icon */}
        <div
          className={`transition-all duration-700 ease-out ${
            phase === "logo" ? "opacity-0 scale-75" : "opacity-100 scale-100"
          }`}
        >
          <div className="relative">
            {/* Glow ring */}
            <div
              className={`absolute inset-0 rounded-2xl transition-all duration-1000 ${
                phase === "text" || phase === "subtitle" || phase === "fadeout"
                  ? "opacity-100 scale-110"
                  : "opacity-0 scale-100"
              }`}
              style={{
                background: "radial-gradient(circle, rgba(255,255,255,0.15) 0%, transparent 70%)",
                filter: "blur(8px)",
              }}
            />
            <img
              src="https://storage.readdy-site.link/project_files/caeb4770-bff8-4a6c-aacf-b2169351eb6e/f9f885c4-cf1a-4153-aa6b-79a8a47d3db7_unnamed.png?v=32f9e45271c38a3f927699f78a81a4f1"
              alt="時間割くん"
              className="w-20 h-20 rounded-2xl object-cover relative z-10"
            />
          </div>
        </div>

        {/* Title */}
        <div
          className={`text-center transition-all duration-700 ease-out ${
            phase === "logo" || phase === "text" && false
              ? "opacity-0 translate-y-4"
              : phase === "text" || phase === "subtitle" || phase === "fadeout"
              ? "opacity-100 translate-y-0"
              : "opacity-0 translate-y-4"
          }`}
          style={{
            opacity: phase === "logo" ? 0 : 1,
            transform: phase === "logo" ? "translateY(16px)" : "translateY(0)",
            transition: "opacity 0.7s ease-out, transform 0.7s ease-out",
          }}
        >
          <h1 className="text-white text-4xl md:text-5xl font-black tracking-tight leading-none">
            時間割くんbot
          </h1>
        </div>

        {/* Divider line */}
        <div
          className="h-px bg-white/20 transition-all duration-700 ease-out"
          style={{
            width: phase === "subtitle" || phase === "fadeout" ? "120px" : "0px",
            transitionDelay: "0.1s",
          }}
        />

        {/* Subtitle */}
        <div
          style={{
            opacity: phase === "subtitle" || phase === "fadeout" ? 1 : 0,
            transform: phase === "subtitle" || phase === "fadeout" ? "translateY(0)" : "translateY(8px)",
            transition: "opacity 0.6s ease-out 0.2s, transform 0.6s ease-out 0.2s",
          }}
        >
          <p className="text-white/40 text-sm tracking-[0.2em] font-light">
            Made by kurekure_botti
          </p>
        </div>
      </div>

      {/* Bottom progress bar */}
      <div className="absolute bottom-0 left-0 w-full h-0.5 bg-white/5">
        <div
          className="h-full bg-white/30 transition-all ease-linear"
          style={{
            width:
              phase === "logo" ? "0%" :
              phase === "text" ? "30%" :
              phase === "subtitle" ? "70%" :
              phase === "fadeout" ? "100%" : "100%",
            transitionDuration:
              phase === "logo" ? "0ms" :
              phase === "text" ? "600ms" :
              phase === "subtitle" ? "2400ms" :
              "800ms",
          }}
        />
      </div>

      <style>{`
        @keyframes float-particle {
          0%, 100% { transform: translateY(0px) translateX(0px); opacity: 0.3; }
          50% { transform: translateY(-20px) translateX(10px); opacity: 0.8; }
        }
      `}</style>
    </div>
  );
}
